import { useReducer, type Dispatch } from 'react';
import type { CrmCommitBody, FieldChoice, MergePolicy, PersonPreview, PreviewScope } from './types';

/**
 * Working state for the p3 Clean/Match/Send flow — the operator's field
 * picks, owner picks, Send-selection overrides, and per-run policy toggle.
 * Lives in the `CrmViews` container (Task 6) so it survives switching
 * between views; NOT persisted, NOT server state (that's `useQuery` in the
 * container — this hook is the pure, closure-free part).
 */
export interface CrmQueueState {
  source: string; // selected CRM source
  view: 'leads' | 'clean' | 'match' | 'send';
  fieldChoices: Record<string, Record<string, FieldChoice>>; // personId → field → choice
  ownerPicks: Record<string, string>; // personId → ownerId
  deselected: Record<string, true>; // Send default = all-ready minus these
  policyOverride: MergePolicy | null; // per-run toggle (null = tenant default)
  /**
   * The preview request's `scope` (spec 4 §3, p3 Task 5). Defaults to
   * `'changed'` — the queue opens on the delta view, which the backend
   * narrows by collapsing already-sent-unchanged rows
   * (`PreviewSummary.collapsed`). `'all'` is the one-way "show them" escape
   * hatch (`showAllPreview` below); there is no dispatch back to `'changed'`
   * because switching scope is a re-request the backend decides, not a
   * client-side filter — see `CrmViews`, which feeds this straight into the
   * preview query key so a scope change always refetches.
   */
  previewScope: PreviewScope;
}

export type CrmQueueAction =
  | { type: 'setView'; view: CrmQueueState['view'] }
  | { type: 'setSource'; source: string }
  | { type: 'pickField'; personId: string; field: string; choice: FieldChoice }
  | { type: 'clearFieldPick'; personId: string; field: string }
  | { type: 'pickOwner'; personId: string; ownerId: string }
  | { type: 'bulkAssignOwner'; personIds: string[]; ownerId: string }
  | { type: 'toggleSelected'; personId: string }
  /**
   * Send's select-all / deselect-all. Scoped to the ids it is handed — the
   * ready rows currently on screen — so a deselection the operator made on a
   * row that has since left that list (no longer ready, or filtered out by
   * the delta scope) is left exactly as they set it. Same reasoning as
   * `resetAfterSend`: a bulk control may never silently re-arm a row the
   * operator deliberately excluded and cannot currently see.
   */
  | { type: 'setAllSelected'; personIds: string[]; selected: boolean }
  | { type: 'setPolicyOverride'; policy: MergePolicy | null }
  /**
   * The Match disclosure line's "show them" affordance (spec 4 §5a). One-way:
   * switches `previewScope` to `'all'` so the next preview re-request asks
   * for everything. Nothing dispatches back to `'changed'` — the way back
   * there is reopening the queue (a fresh mount via `setSource`, or a new
   * conference) rather than a toggle.
   */
  | { type: 'showAllPreview' }
  /**
   * Clears the SENT rows only (`sentIds` = the personIds that actually went
   * out in the commit body), keeps the current view. Rows that stayed behind —
   * deselected, not ready, or simply held back — keep their field picks, their
   * owner picks AND their deselection: a send must never silently re-arm rows
   * the operator deliberately excluded (review fix I2).
   */
  | { type: 'resetAfterSend'; sentIds: string[] };

function initialState(source: string): CrmQueueState {
  return {
    source,
    view: 'leads',
    fieldChoices: {},
    ownerPicks: {},
    deselected: {},
    policyOverride: null,
    previewScope: 'changed',
  };
}

function withoutKey<V>(obj: Record<string, V>, key: string): Record<string, V> {
  if (!(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
}

function reducer(state: CrmQueueState, action: CrmQueueAction): CrmQueueState {
  switch (action.type) {
    case 'setView':
      return { ...state, view: action.view };

    case 'setSource':
      // A new source has its own preview/match-check rows — the previous
      // source's picks and selection no longer apply to anything.
      return { ...initialState(action.source), view: state.view };

    case 'pickField': {
      const personFields = state.fieldChoices[action.personId] ?? {};
      return {
        ...state,
        fieldChoices: {
          ...state.fieldChoices,
          [action.personId]: { ...personFields, [action.field]: action.choice },
        },
      };
    }

    case 'clearFieldPick': {
      const personFields = state.fieldChoices[action.personId];
      if (!personFields || !(action.field in personFields)) return state;
      const nextPersonFields = withoutKey(personFields, action.field);
      const nextFieldChoices =
        Object.keys(nextPersonFields).length === 0
          ? withoutKey(state.fieldChoices, action.personId)
          : { ...state.fieldChoices, [action.personId]: nextPersonFields };
      return { ...state, fieldChoices: nextFieldChoices };
    }

    case 'pickOwner':
      return {
        ...state,
        ownerPicks: { ...state.ownerPicks, [action.personId]: action.ownerId },
      };

    case 'bulkAssignOwner': {
      const nextOwnerPicks = { ...state.ownerPicks };
      for (const personId of action.personIds) {
        nextOwnerPicks[personId] = action.ownerId;
      }
      return { ...state, ownerPicks: nextOwnerPicks };
    }

    case 'toggleSelected': {
      const alreadyDeselected = state.deselected[action.personId];
      const nextDeselected = alreadyDeselected
        ? withoutKey(state.deselected, action.personId)
        : { ...state.deselected, [action.personId]: true as const };
      return { ...state, deselected: nextDeselected };
    }

    case 'setAllSelected': {
      const next = { ...state.deselected };
      let changed = false;
      for (const personId of action.personIds) {
        if (action.selected) {
          if (personId in next) {
            delete next[personId];
            changed = true;
          }
        } else if (!(personId in next)) {
          next[personId] = true as const;
          changed = true;
        }
      }
      // Identity-stable when nothing moved, matching `withoutKey` above —
      // clicking an already-all-selected box must not churn every consumer.
      return changed ? { ...state, deselected: next } : state;
    }

    case 'setPolicyOverride':
      return { ...state, policyOverride: action.policy };

    case 'showAllPreview':
      return { ...state, previewScope: 'all' };

    case 'resetAfterSend': {
      if (action.sentIds.length === 0) return state;
      const fieldChoices = { ...state.fieldChoices };
      const ownerPicks = { ...state.ownerPicks };
      const deselected = { ...state.deselected };
      for (const personId of action.sentIds) {
        delete fieldChoices[personId];
        delete ownerPicks[personId];
        // A sent row is normally absent here (deselected rows aren't sent) —
        // removing it is the cheap way to keep the two in step either way.
        delete deselected[personId];
      }
      return { ...state, fieldChoices, ownerPicks, deselected };
    }

    default:
      return state;
  }
}

export function useCrmQueue(initialSource: string): {
  state: CrmQueueState;
  dispatch: Dispatch<CrmQueueAction>;
} {
  const [state, dispatch] = useReducer(reducer, initialSource, initialState);
  return { state, dispatch };
}

/**
 * ready = bucket 'new' | 'existing', and not held back by company lead rules.
 *
 * Owner used to gate this (see git history) until the backend's owner fallback
 * chain resolved every row. The only non-bucket gate now is `sendBlocked`: in
 * `block` mode a lead below the company's notes minimum (spec 2026-09-14
 * §2.3) or missing a held field (spec 2026-09-28 §6) is refused by the
 * commit anyway, so it belongs under "Not ready" with its reason rather than
 * in the send selection. An absent `completeness` (older backend, rules off)
 * never blocks.
 */
export function isReady(p: PersonPreview): boolean {
  return (p.bucket === 'new' || p.bucket === 'existing') && p.completeness?.sendBlocked !== true;
}

/**
 * Builds the commit body from the current preview batch + working state.
 * selected = ready minus deselected; ownerId = pick ?? suggestedOwnerId,
 * but ONLY when the row requires an owner (§9: other Existing rows keep
 * their real owner — a suggestion or stale pick on them must never reach
 * the wire, where the engine treats any ownerId as a reassignment write);
 * fieldChoices omitted when empty for that person; policy included only
 * when overridden; `expected` passed through VERBATIM (bucket rides along
 * automatically per the p2 ruling).
 */
export function commitBodyFrom(args: {
  previews: PersonPreview[];
  state: CrmQueueState;
}): CrmCommitBody {
  const { previews, state } = args;

  const persons = previews
    .filter((p) => isReady(p) && !state.deselected[p.personId])
    .map((p) => {
      const ownerId = p.ownerRequired
        ? state.ownerPicks[p.personId] ?? p.suggestedOwnerId ?? undefined
        : undefined;
      const fieldChoices = state.fieldChoices[p.personId];
      const hasFieldChoices = fieldChoices && Object.keys(fieldChoices).length > 0;

      return {
        personId: p.personId,
        ...(ownerId ? { ownerId } : {}),
        ...(hasFieldChoices ? { fieldChoices } : {}),
        expected: p.expected,
      };
    });

  return {
    ...(state.policyOverride ? { policy: state.policyOverride } : {}),
    persons,
  };
}

/**
 * What a `setSource` would discard. The reducer resets to `initialState` on a
 * source switch (by design — the other CRM's rows are different rows), so this
 * is the caller's only way to ask whether that reset costs the operator
 * anything before it happens. Counts PEOPLE, not individual picks: "2 field
 * choices" means two people, which is what the operator sees on Match.
 */
export interface QueueWork {
  ownerPicks: number;
  fieldChoices: number;
  deselected: number;
  policyChanged: boolean;
}

export function queueWork(state: CrmQueueState): QueueWork {
  return {
    ownerPicks: Object.keys(state.ownerPicks).length,
    fieldChoices: Object.keys(state.fieldChoices).length,
    deselected: Object.keys(state.deselected).length,
    policyChanged: state.policyOverride !== null,
  };
}

export function hasQueueWork(work: QueueWork): boolean {
  return (
    work.ownerPicks > 0 || work.fieldChoices > 0 || work.deselected > 0 || work.policyChanged
  );
}
